/**
 * JWT helpers — decode-only, never verify.
 *
 * Per the data-layer handoff (§5): the frontend MUST NOT verify the JWT
 * signature client-side. There's no JWKS endpoint and no public key
 * exposed. We only decode the payload to read the `exp` claim (so we can
 * pre-empt expiry by ~10s) and optionally `lang` for i18n.
 *
 * Storage and the keys we use live in `api/storage.ts`; this file is just
 * the type-safe payload reader.
 */
import { jwtDecode } from 'jwt-decode';

/**
 * The shape of the portal-issued JWT.
 *
 * The two source-of-truth handoffs disagree on claim names:
 *
 *   - UI_REWRITE_HANDOFF §2.2 lists Cognito-style claims:
 *     `sub`, `cognito:groups[]`, `tenant_id`, `sid`
 *   - UI_DATA_LAYER_HANDOFF §5 lists short-form claims governed by the
 *     "JWT Token Architecture — Patient Chatbot ↔ Patient Portal" spec:
 *     `sub`, `cid`, `tid`, `ck`, `env`, `ch`, `lang`, `ph`, `auth`
 *
 * The frontend only reads `exp` (universal across both specs), so we type
 * everything else as optional. If a future iteration needs more claims,
 * read them through this type — don't widen the read surface.
 *
 * NEVER verify the signature client-side. There's no JWKS endpoint exposed
 * to the patient portal, and verification is the backend's job on every
 * request anyway.
 */
export interface PortalJWTClaims {
  /** Standard JWT — present in both specs. */
  sub: string;
  iss?: string;
  aud?: string;
  iat?: number;
  /** Unix seconds. The ONLY claim the frontend reads. */
  exp: number;

  // UI_DATA_LAYER short-form claims (chatbot-architecture spec)
  cid?: string;
  tid?: string;
  ck?: string;
  env?: string;
  ch?: string;
  /** Patient's preferred language; "en" or "es". */
  lang?: string;
  ph?: string;
  auth?: string;

  // UI_REWRITE Cognito-style claims
  /** 
   * Defensive-only legacy typing. Patients do not have Cognito groups. 
   * Preserved strictly in case backend attaches it for chatbot-compat; 
   * the FE NEVER reads this claim.
   */
  'cognito:groups'?: string[];
  tenant_id?: string;
  sid?: string;
}

/** Decode a token's `exp` claim into a JS-style epoch milliseconds value. */
export function getTokenExpMs(token: string): number {
  try {
    const claims = jwtDecode<PortalJWTClaims>(token);
    return claims.exp * 1000;
  } catch {
    // Malformed token — treat as expired so the caller redirects to login.
    return 0;
  }
}

/** Read the `lang` claim if present. Returns `null` when missing or malformed. */
export function getTokenLang(token: string): 'en' | 'es' | null {
  try {
    const claims = jwtDecode<PortalJWTClaims>(token);
    if (claims.lang === 'en' || claims.lang === 'es') return claims.lang;
    return null;
  } catch {
    return null;
  }
}
